import { useEffect, useState } from 'react';

export default function useEscalationsShellState({ currentUser, isActualAdmin, isProcessing }) {
    const [availability, setAvailability] = useState(null);
    const [availabilityLoaded, setAvailabilityLoaded] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const canLoadUnread = !isProcessing && Boolean(isActualAdmin || availability?.availableToCurrentUser);

    useEffect(() => {
        if (!currentUser || isProcessing) {
            setAvailability(null);
            setAvailabilityLoaded(Boolean(isProcessing));
            return undefined;
        }

        let ignore = false;
        const loadAvailability = async () => {
            try {
                const response = await fetch('/api/escalations/availability');
                const data = await response.json();
                if (ignore) return;
                if (!response.ok || !data.success) {
                    throw new Error(data.error || 'Escalations availability is unavailable.');
                }
                setAvailability(data.availability || null);
            } catch (error) {
                if (!ignore) {
                    setAvailability({
                        availableToCurrentUser: isActualAdmin,
                        agentAccessEnabled: false,
                        isAdmin: isActualAdmin,
                        unavailable: true
                    });
                }
            } finally {
                if (!ignore) setAvailabilityLoaded(true);
            }
        };

        loadAvailability();
        const interval = setInterval(loadAvailability, 30000);
        return () => {
            ignore = true;
            clearInterval(interval);
        };
    }, [currentUser, isActualAdmin, isProcessing]);

    useEffect(() => {
        if (!currentUser || !availabilityLoaded || !canLoadUnread) {
            setUnreadCount(0);
            return undefined;
        }

        let ignore = false;
        const loadUnreadSummary = async () => {
            try {
                const response = await fetch('/api/escalations/unread-summary?limit=5');
                const data = await response.json();
                if (!ignore && response.ok && data.success) {
                    setUnreadCount(Number(data.totalUnreadEscalations || 0));
                }
            } catch (error) {
                // Unread badges are non-blocking and fail quietly.
            }
        };
        const handleUnreadChange = (event) => {
            const count = event?.detail?.totalUnreadEscalations;
            if (Number.isFinite(Number(count))) {
                setUnreadCount(Number(count));
            } else {
                loadUnreadSummary();
            }
        };

        loadUnreadSummary();
        window.addEventListener('escalations-unread-changed', handleUnreadChange);
        const interval = setInterval(loadUnreadSummary, 60000);
        return () => {
            ignore = true;
            clearInterval(interval);
            window.removeEventListener('escalations-unread-changed', handleUnreadChange);
        };
    }, [availabilityLoaded, canLoadUnread, currentUser]);

    return {
        availability,
        setAvailability,
        availabilityLoaded,
        unreadCount
    };
}
